import UserContext from "Context/UserProvider";
import { useContext, useEffect, useReducer, useState } from "react";
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from "react-router";
import { useApiService } from '../../../services/ApiService/ApiService';
import { 
    Box,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Stack,
    CircularProgress,
    Autocomplete,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

function reducer(state: any, action: any) {
    switch (action.input) {
        case "businessUuid":
            return { ...state, businessUuid: action.value };
        case "fleetName":
            return { ...state, fleetName: action.value };
        default:
            return state;
    }
}

function checking_null_undefined(array: any) {
    if (array === null || array === undefined) {
        return [];
    }
    return array;
}

function AddFleetTabScreen(props: any) {
    const businesses = checking_null_undefined(props.businesses);
    const { userInfo, setUserInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(false);
    const location = useLocation() as any;
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [modalIsOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();

    const businessOptions = businesses.map((e: any) => ({
        label: e.name,
        value: e.uuid,
    }));

    const initialBusiness = location.state?.business
        ? {
            label: location.state.business.name,
            value: location.state.business.uuid,
        }
        : businessOptions[0];

    const initialState = {
        businessUuid: initialBusiness.value,
        fleetName: "",
    };
    
    const [state, dispatch] = useReducer(reducer, initialState);
    const [selectedBusiness, setSelectedBusiness] = useState(initialBusiness);

    const handleClose = () => {
        setIsOpen(false);
    };

    const handleBusinessChange = (event: any, newValue: any) => {
        if (newValue) {
            dispatch({
                input: 'businessUuid',
                value: newValue.value,
            });
            setSelectedBusiness(newValue);
        }
    };

    const handleFleetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            input: 'fleetName',
            value: e.target.value,
        });
    };

    const saveFleet = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/fleet/create', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    fleetUuid: userInfo.fleetUuid,
                    businessUuid: state.businessUuid,
                    name: state.fleetName,
                    makeActiveOnceCreated: active
                })
            });
            
            const res_json = await response.json();

            if (response.ok) {
                toast.success('Fleet saved successfully', {
                    position: 'bottom-center',
                    duration: 5000,
                });

                if (active) {
                    setUserInfo((prev: any) => ({
                        ...prev,
                        businessUuid: state.businessUuid,
                        fleetUuid: res_json.fleet.uuid,
                        currentFleetName: res_json.fleet.name,
                        businessName: res_json.fleet.businessName,
                    }));
                    navigate("/plan");
                }

                dispatch({
                    input: "fleetName",
                    value: "",
                });
                setIsOpen(true);
            } else {
                const errorMsg = res_json.code 
                    ? `An error (${res_json.code}) occurred whilst saving this business.`
                    : "An unexpected error occurred.";
                setErrorMessage(errorMsg);
                toast.error(errorMsg, {
                    position: 'bottom-center',
                    duration: 5000,
                });
            }
        } catch (error) {
            console.error('Error saving fleet:', error);
            toast.error('Failed to save fleet. Please try again.', {
                position: 'bottom-center',
                duration: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Stack spacing={3}>
                <Autocomplete
                    options={businessOptions}
                    value={selectedBusiness}
                    onChange={handleBusinessChange}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Business"
                            required
                        />
                    )}
                />

                <TextField
                    fullWidth
                    label="Fleet Name"
                    value={state.fleetName}
                    onChange={handleFleetNameChange}
                    name="fleetName"
                    required
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={active}
                            onChange={(e) => setActive(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Make active once created"
                />

                <Button
                    variant="contained"
                    onClick={saveFleet}
                    disabled={loading}
                    fullWidth
                >
                    {loading ? <CircularProgress size={24} /> : 'Create Fleet'}
                </Button>
            </Stack>

            <Dialog
                open={modalIsOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Success
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography align="center" variant="h5" sx={{ my: 3 }}>
                        Thank you for creating a new fleet with us!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AddFleetTabScreen;
